import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import {
  Card, Table, EmptyState, StatusPill,
} from '../../../design-system';
import { money, dayTime } from '../../../lib/tallyBooks';
import TallyLedgerStatement from './TallyLedgerStatement';
import '../../styles/TallyPanel.css';

// What shape this client is in, as at the last sync.
//
// ⚠️ THIS IS THE ONE TABLE ON THE CLIENT'S TALLY TAB, and that is a decision
// rather than an omission. The ledger list, the entry list, the Tally-computed
// reports and the party-matching queue all moved to the firm-level Tally page,
// where a firm works across every company at once. What is left here is the
// question somebody actually opens a CLIENT record to ask: what do they hold,
// what do they owe, and is anything obviously wrong.
//
// ⚠️ SUMMED FROM THE MIRROR, NOT ASKED OF TALLY. It renders immediately with
// nothing queued and no requirement that the client's PC be awake — see the
// header of server-side utils/tally/position.js. The cost is that it is only as
// fresh as the last sync, which is why the age is rendered above the figures
// and not below them.
//
// ⚠️ TALLY'S SIGNS ARE NOT TRANSLATED. A credit balance arrives negative and is
// shown in parentheses, the accounting convention for the same fact. We do not
// relabel it "Cr" — that would be OUR reading of their convention, and the
// moment the reading is wrong for one account type every figure under it is
// arguable. See client/src/lib/tallyBooks.js.

const SECTIONS = [
  {
    key: 'assets',
    title: 'What they hold',
    // Deliberately not "Assets". These are the classified debit-balance heads
    // off a mirror of unknown completeness, not a balance sheet somebody signed.
    note: 'Debit balances, as Tally reports them.',
  },
  {
    key: 'liabilities',
    title: 'What they owe',
    note: 'Credit balances, shown in parentheses.',
  },
  {
    key: 'pnl',
    title: 'Trading & profit and loss',
    // ⚠️ SHOWN, BUT NEVER IN THE POSITION TOTAL. Turnover is a flow over a
    // period, not something held on a date, and folding it in is how a
    // "position" comes to count a year's sales as though it were an asset.
    note: 'A flow over the year, not a balance held on a date — so it is listed apart and never added into the position.',
  },
  {
    key: 'other',
    title: 'Not classified',
    // ⚠️ ALWAYS RENDERED WHEN NON-EMPTY. Tally reports the ledger's IMMEDIATE
    // group, so a firm's own group naming can defeat every rule the server
    // matches on. A total that quietly excludes those ledgers is a total
    // somebody reconciles against Tally and cannot make agree.
    note: 'These ledgers sit under a group we could not place. They are excluded from the totals above — check them in Tally.',
  },
];

/** Negative is a credit balance; accountants read that as parentheses. */
function Amount({ value, strong }) {
  const n = Number(value) || 0;
  const text = n < 0 ? `(${money(Math.abs(n))})` : money(n);
  return (
    <span className={`tally-num${n < 0 ? ' tally-num-cr' : ''}${strong ? ' tally-num-strong' : ''}`}>
      {text}
    </span>
  );
}

function TallyPosition({ connectionId, connection }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openHead, setOpenHead] = useState(null);
  const [openLedgerId, setOpenLedgerId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/tally/connections/${connectionId}/position`);
      setData(res.data || null);
    } catch {
      // Leave the last known figures rather than blanking them on a blip — a
      // transient 401 during a token refresh must not read as "empty books".
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Card><p className="tally-muted">Reading the books…</p></Card>;

  if (!data || !data.ledgerCount) {
    return (
      <EmptyState
        icon="📗"
        title="No ledgers mirrored yet"
        description="The position is built from this client's chart of accounts. It fills in on the first successful sync — if the last attempt failed, the reason is at the top of this tab."
      />
    );
  }

  const { heads, totals } = data;
  const asAt = connection?.lastSuccessfulSyncAt;

  return (
    <>
      <Card>
        <div className="tally-head">
          <div>
            <h3 className="tally-section-title">Financial position</h3>
            {/* ⚠️ THE DATE IS THE SYNC, NOT TODAY, and it is said in those words.
                "As at today" over week-old numbers is the single most expensive
                thing this screen could imply. */}
            <p className="tally-muted">
              {asAt
                ? `As at the last sync — ${dayTime(asAt)}. Summed from ${data.ledgerCount} ledgers Tally reported; nothing here is recomputed by us.`
                : 'These books have never synced, so there is nothing to report a position from.'}
            </p>
          </div>
          {connection?.stale && (
            <StatusPill variant="danger" label="Figures may be out of date" />
          )}
        </div>

        <div className="tally-position-totals">
          <div>
            <span className="tally-kpi-label">What they hold</span>
            <Amount value={totals.assets} strong />
          </div>
          <div>
            <span className="tally-kpi-label">What they owe</span>
            <Amount value={totals.liabilities} strong />
          </div>
          <div>
            {/* ⚠️ NOT LABELLED "NET WORTH". It is the arithmetic sum of what we
                could classify, and naming it net worth invites somebody to put
                it in front of a client as though it were a signed figure. */}
            <span className="tally-kpi-label">Net of the two</span>
            <Amount value={totals.classified} strong />
          </div>
        </div>

        {/* ⚠️ SAID OUT LOUD WHEN THE SIGNS WERE TURNED ROUND. This firm's export
            carries a debit balance NEGATIVE, so the Ledgers list shows its
            debtors as −10,49,624 while this table shows them as money held.
            Both are right and they look like they contradict each other, so the
            reason is on screen rather than left to be discovered. */}
        {data.signConvention === 'debit_negative' && (
          <p className="tally-muted">
            These books report a debit balance as a negative number. The figures
            here are turned round to read the usual way — money held positive,
            money owed in parentheses — so they will not match the sign shown in
            the Ledgers list, only its magnitude.
          </p>
        )}

        {!!totals.unclassified && (
          <p className="tally-warn">
            {`${money(Math.abs(totals.unclassified))} sits under groups we could not place, and is not in the figures above. It is listed under "Not classified" below.`}
          </p>
        )}
      </Card>

      {SECTIONS.map((section) => {
        const rows = heads.filter((h) => h.section === section.key);
        if (!rows.length) return null;
        return (
          <Card key={section.key}>
            <div className="tally-head">
              <div>
                <h3 className="tally-section-title">{section.title}</h3>
                <p className="tally-muted">{section.note}</p>
              </div>
            </div>

            <Table
              columns={[
                {
                  key: 'head',
                  header: 'Head',
                  render: (h) => (
                    // The heads are the report; the ledgers under them are
                    // detail somebody asks for one head at a time. Expanding in
                    // place keeps the shape of the balance sheet on screen,
                    // which a modal would replace.
                    <button
                      type="button"
                      className="tally-linkish tally-linkish-inline"
                      onClick={() => setOpenHead(openHead === h.key ? null : h.key)}
                      aria-expanded={openHead === h.key}
                    >
                      {openHead === h.key ? '▾ ' : '▸ '}
                      {h.label}
                    </button>
                  ),
                },
                {
                  key: 'count',
                  header: 'Ledgers',
                  render: (h) => <span className="tally-num">{h.count}</span>,
                },
                {
                  key: 'total',
                  header: 'Closing balance',
                  render: (h) => <Amount value={h.total} strong />,
                },
              ]}
              data={rows}
              keyField="key"
            />

            {rows.map((h) => (openHead === h.key ? (
              <div className="tally-position-detail" key={h.key}>
                <Table
                  columns={[
                    {
                      key: 'name',
                      header: 'Ledger',
                      // The last step of the drill: head → its ledgers → this
                      // ledger's own transactions. A balance in a position is
                      // only ever the start of the question.
                      render: (l) => (l.id ? (
                        <button
                          type="button"
                          className="tally-linkish tally-linkish-inline"
                          onClick={() => setOpenLedgerId(l.id)}
                        >
                          {l.name}
                        </button>
                      ) : l.name),
                    },
                    { key: 'parent', header: 'Group', render: (l) => l.parent || '—' },
                    {
                      key: 'opening',
                      header: 'Opening',
                      render: (l) => <Amount value={l.openingBalance} />,
                    },
                    {
                      key: 'closing',
                      header: 'Closing',
                      render: (l) => <Amount value={l.closingBalance} />,
                    },
                  ]}
                  data={h.ledgers}
                  keyField="name"
                />
                {/* ⚠️ SAID OUT LOUD. The head's total is over EVERY ledger in
                    it, the list is the largest few — so without this line the
                    rows visibly fail to add up to the total beside them, which
                    reads as a broken figure rather than a shortened list. */}
                {h.hiddenCount > 0 && (
                  <p className="tally-muted">
                    {`Showing the ${h.ledgers.length} largest of ${h.count}. The head total above covers all ${h.count} — see the firm's Tally page for the full ledger list.`}
                  </p>
                )}
              </div>
            ) : null))}
          </Card>
        );
      })}

      {openLedgerId && (
        <TallyLedgerStatement
          connectionId={connectionId}
          ledgerId={openLedgerId}
          onClose={() => setOpenLedgerId(null)}
        />
      )}
    </>
  );
}

export default TallyPosition;
